import { InputHTMLAttributes, forwardRef, useMemo, FocusEventHandler } from 'react';

import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import {
    CalendarOutlinedSize16,
    ClockOutlinedSize16,
    CalendarOutlinedSize24,
    ClockOutlinedSize24,
} from '@hh.ru/magritte-ui-icon/icon';
import { BaseInput } from '@hh.ru/magritte-ui-input/BaseInput';
import { ControlledDateTimeInputProps } from '@hh.ru/magritte-ui-input/types';
import { useDateTimeMask } from '@hh.ru/magritte-ui-input/useDateTimeMask';

export const DateTimeInput = forwardRef<
    HTMLInputElement,
    ControlledDateTimeInputProps & Omit<InputHTMLAttributes<HTMLInputElement>, keyof ControlledDateTimeInputProps>
>(({ dateMask, timeMask, timeRounding, onButtonClick, hideMask, size, ...props }, ref) => {
    const maskedInputRef = useDateTimeMask(dateMask, timeMask, timeRounding);
    const inputRef = useMultipleRefs(maskedInputRef, ref);

    const onBlur: FocusEventHandler<HTMLInputElement> | undefined =
        dateMask === 'dd'
            ? (event) => {
                  if (props.value.length === 1) {
                      if (props.value === '0') {
                          props.onChange('');
                      } else {
                          props.onChange(props.value.padStart(2, '0'));
                      }
                  }
                  props.onBlur?.(event);
              }
            : props.onBlur;

    const maskedPlaceholder = useMemo(() => {
        if (hideMask) {
            return undefined;
        }
        const parts = [];

        if (dateMask) {
            parts.push(dateMask.replace(/d/g, 'Д').replace(/m/g, 'М').replace(/y/g, 'Г'));
        }
        if (timeMask) {
            parts.push('00:00');
        }

        return parts.join(', ');
    }, [dateMask, timeMask, hideMask]);

    return (
        <BaseInput
            type="text"
            inputMode="decimal"
            fixedPlaceholder={maskedPlaceholder}
            {...props}
            {...((onButtonClick &&
                (size === 'small'
                    ? {
                          buttonIcon: timeMask && !dateMask ? ClockOutlinedSize16 : CalendarOutlinedSize16,
                          onButtonClick,
                      }
                    : {
                          buttonIcon: timeMask && !dateMask ? ClockOutlinedSize24 : CalendarOutlinedSize24,
                          onButtonClick,
                      })) ??
                {})}
            ref={inputRef}
            size={size}
            autoComplete="off"
            onBlur={onBlur}
        />
    );
});

DateTimeInput.displayName = 'DateTimeInput';
